import { useEffect, useState } from "react";
import {
  ShieldCheck, Upload, Trash2, Save, Plug, FileCode2, RefreshCw, Receipt, AlertTriangle,
} from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { money, fecha } from "@/lib/format";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const AMBIENTES = [
  { v: "testecf", label: "TesteCF · Pruebas" },
  { v: "certecf", label: "CerteCF · Certificación" },
  { v: "produccion", label: "Producción" },
];

const COLOR_ESTADO = {
  aceptado: "secondary", recibido: "default", simulado: "outline",
  error: "destructive", rechazado: "destructive",
};

export default function AdminECF() {
  const [cfg, setCfg] = useState(null);
  const [comprobantes, setComprobantes] = useState([]);
  const [pendientes, setPendientes] = useState([]);
  const [cert, setCert] = useState({ nombre: "", archivo_base64: "", password: "" });
  const [emitir, setEmitir] = useState(null);
  const [busy, setBusy] = useState(false);

  const cargar = () => {
    api.get("/ecf/configuracion").then((r) => setCfg(r.data)).catch(() => setCfg({}));
    api.get("/ecf/comprobantes").then((r) => setComprobantes(r.data)).catch(() => {});
    api.get("/ecf/facturas-pendientes").then((r) => setPendientes(r.data)).catch(() => {});
  };

  useEffect(cargar, []);

  const guardar = async (extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put("/ecf/configuracion", {
        ambiente: cfg.ambiente, rnc: cfg.rnc, razon_social: cfg.razon_social,
        direccion: cfg.direccion || "", activo: cfg.activo, secuencias: cfg.secuencias, ...extra,
      });
      setCfg(data);
      toast.success("Configuración e-CF guardada");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const subirArchivo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCert({ ...cert, nombre: file.name, archivo_base64: reader.result });
    reader.readAsDataURL(file);
  };

  const guardarCert = async () => {
    if (!cert.archivo_base64) return toast.error("Seleccione el archivo .p12 del certificado");
    if (!cert.password) return toast.error("Escriba la contraseña del certificado");
    setBusy(true);
    try {
      const { data } = await api.post("/ecf/certificado", cert);
      setCfg(data);
      setCert({ nombre: "", archivo_base64: "", password: "" });
      toast.success("Certificado digital cargado y validado");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const borrarCert = async () => {
    try {
      const { data } = await api.delete("/ecf/certificado");
      setCfg(data);
      toast.success("Certificado eliminado del sistema");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const probar = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ecf/probar-conexion");
      toast.success(`Conexión establecida con la DGII (${data.ambiente}). Token válido.`);
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy(false);
  };

  const emitirEcf = async () => {
    if (!emitir.factura_id) return toast.error("Seleccione la factura");
    setBusy(true);
    try {
      const { data } = await api.post("/ecf/emitir", emitir);
      toast.success(`e-CF ${data.encf} generado (${data.modo === "real" ? "enviado a la DGII" : "modo simulación"})`);
      setEmitir(null);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
      cargar();
    }
    setBusy(false);
  };

  const consultar = async (c) => {
    try {
      const { data } = await api.get(`/ecf/comprobantes/${c.id}/estado`);
      toast.success(`Estado: ${data.estado}${data.mensaje ? ` · ${data.mensaje}` : ""}`);
      cargar();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setSecuencia = (tipo, campo, valor) => setCfg({
    ...cfg,
    secuencias: { ...cfg.secuencias, [tipo]: { ...cfg.secuencias[tipo], [campo]: Number(valor) } },
  });

  if (!cfg) return <Loading />;

  const real = cfg.modo === "real";

  return (
    <div data-testid="admin-ecf">
      <PageTitle title="Facturación electrónica e-CF (DGII)"
                 subtitle="Conexión directa con la DGII para emitir comprobantes fiscales electrónicos.">
        <Button className="rounded-full" onClick={() => setEmitir({ tipo: "32", factura_id: "" })}
                data-testid="abrir-emitir-ecf">
          <Receipt className="mr-2 h-4 w-4" /> Emitir e-CF
        </Button>
      </PageTitle>

      <div className={`rounded-lg border p-5 ${real ? "border-emerald-500/50 bg-emerald-500/5"
        : "border-amber-500/50 bg-amber-500/5"}`} data-testid="estado-modulo">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant={real ? "secondary" : "outline"} data-testid="modo-ecf">
            {real ? "Modo real · conectado a la DGII" : "Modo simulación"}
          </Badge>
          <span className="text-xs text-muted-foreground">{cfg.url_ambiente}</span>
          <span className="text-xs text-muted-foreground">{cfg.emitidos ?? 0} e-CF generados</span>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {real
            ? "Cada e-CF se firma con el certificado digital del colegio y se envía a la DGII; el TrackId permite consultar su estado."
            : "El sistema genera el XML e-CF, el código de seguridad y el QR, pero no los envía a la DGII. Cargue el certificado digital .p12 y active el envío real para completar la conexión."}
        </p>
      </div>

      <section className="mt-8 rounded-lg border border-border/60 bg-card p-6">
        <h2 className="font-display text-lg font-semibold">Datos del emisor</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>RNC del colegio</Label>
            <Input value={cfg.rnc || ""} data-testid="ecf-rnc" placeholder="130123456"
                   onChange={(e) => setCfg({ ...cfg, rnc: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Razón social (como está en la DGII)</Label>
            <Input value={cfg.razon_social || ""} data-testid="ecf-razon-social"
                   onChange={(e) => setCfg({ ...cfg, razon_social: e.target.value })} />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Dirección fiscal</Label>
            <Input value={cfg.direccion || ""} data-testid="ecf-direccion"
                   onChange={(e) => setCfg({ ...cfg, direccion: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label>Ambiente DGII</Label>
            <select value={cfg.ambiente} data-testid="ecf-ambiente"
                    onChange={(e) => setCfg({ ...cfg, ambiente: e.target.value })}
                    className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
              {AMBIENTES.map((a) => <option key={a.v} value={a.v}>{a.label}</option>)}
            </select>
          </div>
          <label className="flex items-end gap-3 pb-2 text-sm">
            <Checkbox checked={!!cfg.activo} data-testid="ecf-activo"
                      onCheckedChange={(v) => setCfg({ ...cfg, activo: !!v })} />
            Enviar los e-CF a la DGII (modo real)
          </label>
        </div>
        <Button className="mt-5 rounded-full" onClick={() => guardar()} disabled={busy}
                data-testid="guardar-ecf-config">
          <Save className="mr-2 h-4 w-4" /> Guardar configuración
        </Button>
      </section>

      <section className="mt-8 rounded-lg border border-border/60 bg-card p-6">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <h2 className="font-display text-lg font-semibold">Certificado digital tributario (.p12)</h2>
        </div>
        {cfg.certificado_cargado ? (
          <div className="mt-4 space-y-2 rounded-md border border-border bg-muted/30 p-4 text-sm"
               data-testid="cert-cargado">
            <p className="font-medium">{cfg.certificado_nombre}</p>
            <p className="text-xs text-muted-foreground">{cfg.certificado_titular}</p>
            <p className="text-xs text-muted-foreground">
              Vence: {cfg.certificado_vence ? fecha(cfg.certificado_vence) : "—"}
            </p>
            <div className="flex flex-wrap gap-2 pt-2">
              <Button size="sm" variant="outline" className="rounded-full" onClick={probar} disabled={busy}
                      data-testid="probar-conexion">
                <Plug className="mr-2 h-3 w-3" /> Probar conexión con la DGII
              </Button>
              <Button size="sm" variant="ghost" onClick={borrarCert} data-testid="borrar-certificado">
                <Trash2 className="mr-1 h-3 w-3 text-destructive" /> Eliminar
              </Button>
            </div>
          </div>
        ) : (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Archivo del certificado (.p12 / .pfx)</Label>
              <Input type="file" accept=".p12,.pfx" onChange={subirArchivo} data-testid="ecf-cert-archivo" />
              {cert.nombre && <p className="text-xs text-muted-foreground">{cert.nombre}</p>}
            </div>
            <div className="space-y-2">
              <Label>Contraseña del certificado</Label>
              <Input type="password" value={cert.password} data-testid="ecf-cert-password"
                     onChange={(e) => setCert({ ...cert, password: e.target.value })} />
            </div>
            <Button className="rounded-full sm:col-span-2" onClick={guardarCert} disabled={busy}
                    data-testid="guardar-certificado">
              <Upload className="mr-2 h-4 w-4" /> Cargar certificado
            </Button>
            <p className="text-xs leading-relaxed text-muted-foreground sm:col-span-2">
              El certificado se guarda solo en el servidor y nunca se devuelve al navegador. Debe ser el
              Certificado Digital para Procedimientos Tributarios emitido por una entidad autorizada por INDOTEL.
            </p>
          </div>
        )}
      </section>

      <section className="mt-8 rounded-lg border border-border/60 bg-card p-6">
        <h2 className="font-display text-lg font-semibold">Secuencias e-NCF autorizadas</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Rangos aprobados en la Oficina Virtual de la DGII para cada tipo de comprobante.
        </p>
        <div className="mt-4 space-y-4">
          {Object.entries(cfg.tipos || {}).map(([tipo, nombre]) => {
            const s = cfg.secuencias?.[tipo] || {};
            const restantes = Math.max(0, (s.hasta || 0) - (s.actual || 0) + 1);
            return (
              <div key={tipo} className="grid gap-3 rounded-md border border-border/60 p-4 sm:grid-cols-5"
                   data-testid={`secuencia-${tipo}`}>
                <div className="sm:col-span-2">
                  <p className="text-sm font-semibold">E{tipo}</p>
                  <p className="text-xs text-muted-foreground">{nombre}</p>
                  <p className="mt-1 font-mono text-[10px] text-muted-foreground">
                    Próximo: E{tipo}{String(s.actual || 1).padStart(10, "0")} · {restantes} disponibles
                  </p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Desde</Label>
                  <Input type="number" value={s.desde ?? 1} data-testid={`desde-${tipo}`}
                         onChange={(e) => setSecuencia(tipo, "desde", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Hasta</Label>
                  <Input type="number" value={s.hasta ?? 0} data-testid={`hasta-${tipo}`}
                         onChange={(e) => setSecuencia(tipo, "hasta", e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Próximo</Label>
                  <Input type="number" value={s.actual ?? 1} data-testid={`actual-${tipo}`}
                         onChange={(e) => setSecuencia(tipo, "actual", e.target.value)} />
                </div>
              </div>
            );
          })}
        </div>
        <Button className="mt-5 rounded-full" onClick={() => guardar()} disabled={busy}
                data-testid="guardar-secuencias">
          <Save className="mr-2 h-4 w-4" /> Guardar secuencias
        </Button>
      </section>

      <section className="mt-10">
        <h2 className="font-display text-lg font-semibold">Comprobantes electrónicos emitidos</h2>
        {comprobantes.length === 0 ? (
          <div className="mt-4"><Empty testId="ecf-vacio">Todavía no se ha generado ningún e-CF.</Empty></div>
        ) : (
          <div className="mt-4 overflow-x-auto rounded-lg border border-border/60 bg-card">
            <table className="w-full min-w-[820px] text-sm" data-testid="tabla-ecf">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-6 py-3">e-NCF</th>
                  <th className="px-6 py-3">Tipo</th>
                  <th className="px-6 py-3">Estudiante / tutor</th>
                  <th className="px-6 py-3">Monto</th>
                  <th className="px-6 py-3">Estado</th>
                  <th className="px-6 py-3">TrackId</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody>
                {comprobantes.map((c) => (
                  <tr key={c.id} className="border-t border-border/60" data-testid={`ecf-${c.encf}`}>
                    <td className="px-6 py-4 font-mono text-xs">{c.encf}</td>
                    <td className="px-6 py-4 text-xs">E{c.tipo}</td>
                    <td className="px-6 py-4 text-xs">
                      {c.estudiante_nombre || "—"}
                      <span className="block text-muted-foreground">{c.tutor_nombre}</span>
                    </td>
                    <td className="px-6 py-4">{money(c.monto_total)}</td>
                    <td className="px-6 py-4">
                      <Badge variant={COLOR_ESTADO[c.estado] || "outline"}>{c.estado}</Badge>
                      {c.error && (
                        <span className="mt-1 flex items-center gap-1 text-[10px] text-destructive">
                          <AlertTriangle className="h-3 w-3" /> {String(c.error).slice(0, 40)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 font-mono text-[10px] text-muted-foreground">{c.track_id}</td>
                    <td className="px-6 py-4">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="outline" className="rounded-full"
                                onClick={() => consultar(c)} data-testid={`consultar-${c.encf}`}>
                          <RefreshCw className="mr-1 h-3 w-3" /> Estado
                        </Button>
                        <a href={`${API}/ecf/comprobantes/${c.id}/xml`} target="_blank" rel="noreferrer"
                           data-testid={`xml-${c.encf}`}
                           className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-xs">
                          <FileCode2 className="h-3 w-3" /> XML
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <Dialog open={!!emitir} onOpenChange={(o) => !o && setEmitir(null)}>
        <DialogContent data-testid="modal-emitir-ecf">
          <DialogHeader>
            <DialogTitle className="font-display">Emitir comprobante electrónico</DialogTitle>
            <DialogDescription>
              Se asigna el próximo e-NCF autorizado y se genera el XML firmado del comprobante.
            </DialogDescription>
          </DialogHeader>
          {emitir && (
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label>Factura</Label>
                <select value={emitir.factura_id} data-testid="ecf-factura"
                        onChange={(e) => setEmitir({ ...emitir, factura_id: e.target.value })}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                  <option value="">Seleccione…</option>
                  {pendientes.map((f) => (
                    <option key={f.id} value={f.id}>
                      {`${f.ncf} · ${f.estudiante_nombre || ""} · ${money(f.monto_total)}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label>Tipo de e-CF</Label>
                <select value={emitir.tipo} data-testid="ecf-tipo"
                        onChange={(e) => setEmitir({ ...emitir, tipo: e.target.value })}
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm">
                  {Object.entries(cfg.tipos || {}).map(([t, n]) => (
                    <option key={t} value={t}>{`E${t} · ${n}`}</option>
                  ))}
                </select>
              </div>
              {emitir.tipo !== "32" && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>RNC / cédula del comprador</Label>
                    <Input value={emitir.rnc_comprador || ""} data-testid="ecf-rnc-comprador"
                           onChange={(e) => setEmitir({ ...emitir, rnc_comprador: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Razón social del comprador</Label>
                    <Input value={emitir.razon_social_comprador || ""}
                           onChange={(e) => setEmitir({ ...emitir, razon_social_comprador: e.target.value })} />
                  </div>
                </div>
              )}
              <Button className="rounded-full py-6" onClick={emitirEcf} disabled={busy}
                      data-testid="confirmar-emitir-ecf">
                <Receipt className="mr-2 h-4 w-4" /> Generar e-CF
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
